import { useState, useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { BankDetailsStatusFilter } from '@percent/admin-dashboard/app/bankDetails/bankDetailsContainer/bankDetailsTable/BankDetailsTable.types'
import {
  TableSearchAndFilters,
  Flex,
  Icon,
  FilterList,
  SearchFilterList,
  DateRange,
  Typography
} from '@percent/lemonade'
import { useOrganisationFilter } from '@percent/admin-dashboard/common/hooks'
import { dayJS } from '@percent/admin-dashboard/common/library/utility/date'
import { BankDetailsFiltersProps } from './BankDetailsFilters.types'
import { LocaleKey } from '@percent/admin-dashboard/i18n/config'
import styles from './BankDetails.module.scss'

const STATUS_OPTIONS = [
  BankDetailsStatusFilter.pending,
  BankDetailsStatusFilter.approved,
  BankDetailsStatusFilter.rejected,
  BankDetailsStatusFilter.deleted
] as const

export function BankDetailsFilters({ queryParams, setQueryParams }: BankDetailsFiltersProps) {
  const { t } = useTranslation()

  const [isDatePickerOpened, setIsDatePickerOpened] = useState(false)

  const {
    organisationOptions,
    selectedOrganisations,
    onOrganisationChange,
    isOrganisationFilterLoading,
    organisationSearch
  } = useOrganisationFilter({
    filterValues: queryParams.organisationIds ?? [],
    onFilterValuesChange: (values: string[]) => {
      setQueryParams({ ...queryParams, organisationIds: values })
    },
    onClearFilterValues: () => {
      setQueryParams({ ...queryParams, organisationIds: undefined })
    }
  })

  const dateRangeQueryParams = useMemo(() => {
    if (queryParams.startDate && queryParams.endDate) {
      return {
        startDate: queryParams.startDate,
        endDate: queryParams.endDate
      }
    }

    if (isDatePickerOpened) {
      const defaultStart = dayJS().format('YYYY-MM-DDTHH:mm:ssZ')
      const defaultEnd = dayJS().format('YYYY-MM-DDTHH:mm:ssZ')

      return {
        startDate: defaultStart,
        endDate: defaultEnd
      }
    }

    return {
      startDate: undefined,
      endDate: undefined
    }
  }, [isDatePickerOpened, queryParams.endDate, queryParams.startDate])

  const statusOptions = useMemo(
    () =>
      STATUS_OPTIONS.map(status => ({
        value: BankDetailsStatusFilter[status],
        label: t(`table.${BankDetailsStatusFilter[status]}` as LocaleKey)
      })),
    [t]
  )

  const statusFilterParam = queryParams.statusFilter
  const rawStatuses = (() => {
    if (Array.isArray(statusFilterParam)) {
      return statusFilterParam
    }

    if (statusFilterParam) {
      return [statusFilterParam]
    }

    return []
  })()

  const selectedStatuses = rawStatuses.filter((s): s is string => s != null)

  const handleStatusChange = (values: string[]) => {
    setQueryParams({
      statusFilter: values
    })
  }

  const handleDateRangeClick = useCallback(() => {
    setIsDatePickerOpened(open => !open)
  }, [])

  return (
    <TableSearchAndFilters>
      <Flex w="100%" justify="end" align="center">
        <SearchFilterList
          options={organisationOptions}
          isLoading={isOrganisationFilterLoading}
          selectedValues={selectedOrganisations}
          onChange={onOrganisationChange}
          searchValue={organisationSearch.organisationSearchValue}
          onSearchChange={organisationSearch.onOrganisationSearchChange}
          handleClearValue={organisationSearch.onClearOrganisationSearchValue}
          onKeyDown={organisationSearch.handleKeyPress}
          onButtonClick={organisationSearch.onOrganisationsSearchButtonClick}
          testId="bank-details-organisation-search-filter"
          buttonText={t('typography.search')}
          placeholder={t('typography.searchByOrganisationNameOrId')}
          label={t('typography.organisation')}
          placement="bottom-end"
          noResultsFoundText={
            organisationSearch.organisationSearchValue.length < 3 ? t('typography.enterAtLeast3Characters') : undefined
          }
        />
        <FilterList
          label={t('table.status' as LocaleKey)}
          options={statusOptions}
          selectedValues={selectedStatuses}
          onChange={handleStatusChange}
          testId="bank-details-status-filter"
        />
        <div className={styles.datePickerContainer} data-testid="bank-details-date-range-filter">
          <DateRange
            open={isDatePickerOpened}
            setOpen={setIsDatePickerOpened}
            queryParams={dateRangeQueryParams}
            setQueryParams={params => {
              if (typeof params === 'object') {
                setQueryParams({
                  ...queryParams,
                  startDate: params.startDate,
                  endDate: params.endDate
                })
                setIsDatePickerOpened(false)
              }
            }}
            applyButtonText={t('button.apply')}
            clearButtonText={t('button.clear')}
            withinPortal={false}
          >
            <div
              onClick={handleDateRangeClick}
              onKeyDown={e => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  handleDateRangeClick()
                }
              }}
              role="button"
              tabIndex={0}
            >
              <Flex align="center" gap={2}>
                <Typography variant="default-medium">{t('typography.requestDate')}</Typography>
                <Icon name={isDatePickerOpened ? 'chevron-up' : 'chevron-down'} size={3} color="black" />
              </Flex>
            </div>
          </DateRange>
        </div>
      </Flex>
    </TableSearchAndFilters>
  )
}
